import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { ClassNames } from '@emotion/core';
import Popover from '@material-ui/core/Popover';
import MenuList from '@material-ui/core/MenuList';
import Downshift from 'downshift';

import style from './Menu.style';
import theme from '../../themes/default';

const getAnchorOrigin = ({ anchorOrigin, above, alignRight }) => ({
  vertical: above ? 'top' : anchorOrigin.vertical,
  horizontal: alignRight ? 'right' : anchorOrigin.horizontal,
});

const getTransformOrigin = ({ transformOrigin, above, alignRight }) => ({
  vertical: above ? 'bottom' : transformOrigin.vertical,
  horizontal: alignRight ? 'right' : transformOrigin.horizontal,
});

const getApplicableClass = (css) => ({ above, elevated, spacing }) => css`
  ${style.paper.base};
  ${above ? style.paper.above({ spacing }) : style.paper.under({ spacing })};
  ${elevated ? style.paper.elevated : style.paper.unelevated};
`;

export default class Menu extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      anchorElement: null,
      highlightedIndex: 0,
      above: props.above,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleTargetClick = this.handleTargetClick.bind(this);
    this.hide = this.hide.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleDownshiftStateChange = this.handleDownshiftStateChange.bind(
      this
    );
    this.handleTab = this.handleTab.bind(this);
    this.getIsOpen = this.getIsOpen.bind(this);
    this.downshift = React.createRef();
    this.popover = React.createRef();
  }

  componentDidMount() {
    const { disableAutoFocus } = this.props;

    if (disableAutoFocus) {
      document.addEventListener('keydown', this.handleTab);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleTab);
  }

  handleTab(e) {
    // Mui 4 broke disableAutoFocus prop of Modal (used by Popover below)
    // This issue isn't fixed until Mui 5. We work around it by also proving disableEnforceFocus to
    // Popover, however that prevents tabbing to the open menu. This handler captures the tab and
    // ensures focus is correctly set. Since it's only wired up when disableAutoFocus is true it
    // doesn't cause issues for other usages.
    // Ref:
    //   - https://github.com/mui/material-ui/issues/17321
    //   - https://github.com/mui/material-ui/pull/21612
    // We can remove this and the disableEnforceFocus when we upgrade to Mui 5.
    const popover = this.popover.current;
    if (
      this.getIsOpen() &&
      e.key === 'Tab' &&
      popover &&
      !popover.contains(document.activeElement)
    ) {
      const firstItem = popover.querySelector('[tabindex="0"]');
      if (firstItem) {
        firstItem.focus();
      }
    }
  }

  handleClose(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ anchorElement: null });
  }

  handleTargetClick(event) {
    const { verticalSwitchThreshold } = this.props;
    event.preventDefault();
    event.stopPropagation();
    this.setState({ anchorElement: event.currentTarget });
    if (verticalSwitchThreshold) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.setState({
        above: rect.top > verticalSwitchThreshold,
      });
    }
  }

  handleSelect(item) {
    const { onSelect } = this.props;
    let shouldClose = true;

    const close = () => {
      this.setState({
        anchorElement: null,
        highlightedIndex: 0,
      });
    };
    const preventClose = () => {
      shouldClose = false;
    };

    onSelect({ item, preventClose });

    if (shouldClose) {
      close();
    }
  }

  handleDownshiftStateChange(changes) {
    const hasHighlightedIndex = typeof changes.highlightedIndex !== 'undefined';
    const closing = changes.isOpen === false;
    if (hasHighlightedIndex && !closing) {
      this.setState({ highlightedIndex: changes.highlightedIndex });
    }
  }

  getIsOpen() {
    return !!this.state.anchorElement || !!this.props.anchorPosition;
  }

  hide() {
    this.setState({ anchorElement: null });
  }

  render() {
    const {
      spacing,
      alignRight,
      anchorOrigin,
      className,
      renderTarget,
      renderChildren,
      elevated,
      classes: { paper: paperClass, menu: menuClass },
      testID,
      ariaLabelledby,
      transformOrigin,
      menuListComponent,
      menuListAttributes,
      anchorReference,
      anchorPosition,
      disableAutoFocus,
      ...otherProps
    } = this.props;
    const { highlightedIndex, above, anchorElement } = this.state;

    const originProps = {
      above,
      alignRight,
      anchorOrigin,
      transformOrigin,
    };

    const isOpen = this.getIsOpen();

    // needs to be excluded from otherProps so that it is not passed to popover
    const {
      above: _,
      verticalSwitchThreshold: _verticalSwitchThreshold,
      ...popoverProps
    } = otherProps;

    // Note: Using emotion `classNames` module to pass className prop to
    // Paper element via `PaperProps`
    return (
      <Downshift
        id={`${testID}-downshift`}
        ref={this.downshift}
        isOpen={isOpen}
        highlightedIndex={highlightedIndex}
        onSelect={this.handleSelect}
        defaultHighlightedIndex={0}
        onStateChange={this.handleDownshiftStateChange}
        labelId={ariaLabelledby}
      >
        {({ getInputProps, getItemProps }) => (
          <div
            className={className}
            id={testID}
            role={undefined}
            aria-expanded={undefined}
            aria-haspopup={undefined}
          >
            {anchorReference !== 'anchorPosition' &&
              renderTarget({
                handleTargetClick: this.handleTargetClick,
                isOpen,
                hide: this.hide,
              })}
            <ClassNames>
              {({ css, cx }) => (
                <Popover
                  PaperProps={{
                    elevation: 0,
                    className: cx(
                      getApplicableClass(css)({ above, elevated, spacing }),
                      paperClass
                    ),
                  }}
                  open={isOpen}
                  anchorEl={anchorElement}
                  onClose={this.handleClose}
                  anchorReference={anchorReference}
                  anchorPosition={anchorPosition}
                  disableAutoFocus={disableAutoFocus}
                  // disableEnforceFocus is required as a work-around for issue
                  // https://github.com/mui/material-ui/issues/17321 where disabledAutoFocus only
                  // works if disableEnforceFocus is true aswell. This can be removed when we upgrade
                  // to Mui 5.x which has a fix for this issue.
                  disableEnforceFocus={disableAutoFocus}
                  ref={this.popover}
                  {...popoverProps}
                  anchorOrigin={getAnchorOrigin(originProps)}
                  transformOrigin={getTransformOrigin(originProps)}
                  {...getInputProps()}
                >
                  <MenuList
                    disablePadding
                    component={menuListComponent}
                    className={menuClass}
                    {...menuListAttributes}
                  >
                    {renderChildren({ getItemProps, highlightedIndex })}
                  </MenuList>
                </Popover>
              )}
            </ClassNames>
          </div>
        )}
      </Downshift>
    );
  }
}

Menu.propTypes = {
  /**
   * Function to render the children of the menu element.
   *
   * Menu uses Downshift for accessible and keyboard navigable
   * contents. The renderChildren function will be called with an
   * object containing:
   *
   * * getItemProps: call and apply all props on every selectable
   * item in the menu. It takes an object with an `item` key. This
   * will be used in the `onSelect` callback prop in order to identify
   * the selected item.
   * * highlightedIndex: index of the selected item in the menu. Should
   * be used to apply special styling.
   */
  renderChildren: PropTypes.func,

  /**
   * If true, menu will display above target
   */
  above: PropTypes.bool,

  /**
   * Defines how distant will menu display above / below target (in pixels )
   */
  spacing: PropTypes.string,

  /**
   * If true, menu will be align right with the right side of target
   */
  alignRight: PropTypes.bool,

  /**
   * If true, increases the box-shadow so that the menu looks higher
   */
  elevated: PropTypes.bool,

  /**
   * Function to render component to use as an anchor. It receives
   * an object as parameter with the following fields:
   * * handleTargetClick: handler to be passed to the target's `onClick`.
   */
  renderTarget: PropTypes.func,

  /**
   * This is the point on the popover which
   * will attach to the anchor's origin.
   *
   * Options:
   * vertical: [top, center, bottom, x(px)];
   * horizontal: [left, center, right, x(px)].
   */
  transformOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf(['left', 'center', 'right']),
    ]).isRequired,
    vertical: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf(['top', 'center', 'bottom']),
    ]).isRequired,
  }),
  /**
   * This is the point on the anchor where the popover's
   * `anchorEl` will attach to. This is not used when the
   * anchorReference is 'anchorPosition'.
   *
   * Options:
   * vertical: [top, center, bottom];
   * horizontal: [left, center, right].
   */
  anchorOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf(['left', 'center', 'right']),
    ]).isRequired,
    vertical: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf(['top', 'center', 'bottom']),
    ]).isRequired,
  }),
  /**
   * This is the position that may be used
   * to set the position of the popover.
   * The coordinates are relative to
   * the application's client area.
   */
  anchorPosition: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
  }),

  /**
   * When set, changes vertical origin from top to bottom when
   * distance of anchor origin to top of the viewport is higher
   * than threshold, or vice versa.
   */
  verticalSwitchThreshold: PropTypes.number,
  /*
   * This determines which anchor prop to refer to to set
   * the position of the popover.
   */
  anchorReference: PropTypes.oneOf(['anchorEl', 'anchorPosition', 'none']),
  /*
   * A string className that will be applied to the root element
   */
  className: PropTypes.string,
  /*
   * Classes for applying styles to sub-elements
   */
  classes: PropTypes.shape({ paper: PropTypes.string, menu: PropTypes.string }),

  testID: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  menuListComponent: PropTypes.string,

  /*
   * The object with menuList attributes
   */
  menuListAttributes: PropTypes.shape(),

  /**
   * Called when an item in the menu has been selected. It receives
   * a params object:
   * * item: item selected
   * * preventClose: if called, the menu is not closed after the handler
   * finishes execution.
   */
  onSelect: PropTypes.func,

  /**
   * If true, menu will not take focus when opened.
   */
  disableAutoFocus: PropTypes.bool,
};

Menu.defaultProps = {
  renderChildren: () => {},
  above: false,
  alignRight: false,
  anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
  anchorPosition: undefined,
  elevated: true,
  transformOrigin: { horizontal: 'left', vertical: 'top' },
  anchorReference: 'anchorEl',
  className: '',
  classes: {},
  testID: undefined,
  ariaLabelledby: '',
  onSelect: () => {},
  menuListComponent: 'div',
  menuListAttributes: {},
  spacing: theme.spacing.xSmall,
  verticalSwitchThreshold: undefined,
  disableAutoFocus: false,
  renderTarget: () => {},
};
